<template>
    <div class="min-h-screen page-bg relative overflow-hidden">

        <!-- Performer side backgrounds -->
        <template v-if="!requiresAgeVerification && casting.is_for_performer && casting.performer">
            <div class="hidden xl:block fixed top-0 left-0 w-64 h-full pointer-events-none" style="z-index: 7;">
                <img :src="casting.performer.thumbnail_url" :alt="casting.performer.name" class="w-full h-full object-cover object-top opacity-30" />
                <div class="absolute inset-0 bg-gradient-to-r from-transparent to-[#16101f]" />
                <div class="absolute inset-0 bg-gradient-to-b from-[#16101f] via-transparent to-[#16101f]" />
            </div>
            <div class="hidden xl:block fixed top-0 right-0 w-64 h-full pointer-events-none" style="transform: scaleX(-1); z-index: 7;">
                <img :src="casting.performer.thumbnail_url" :alt="casting.performer.name" class="w-full h-full object-cover object-top opacity-30" />
                <div class="absolute inset-0 bg-gradient-to-r from-transparent to-[#16101f]" />
                <div class="absolute inset-0 bg-gradient-to-b from-[#16101f] via-transparent to-[#16101f]" />
            </div>
        </template>

        <div class="relative" style="z-index: 10;">
            <HeaderV2
                :isUserLoggedIn="isUserLoggedIn"
                :isGoldUser="isGoldUser"
                :routePrefix="routePrefix"
                :requiresAgeVerification="requiresAgeVerification"
                @goldJoinClick="isBuyStarsModalOpen = true"
            />
        </div>

        <!-- Hero banner -->
        <div class="relative overflow-hidden border-b border-violet-500/20" :class="casting.is_for_performer && casting.performer ? 'xl:mx-auto xl:max-w-[calc(100%-32rem)]' : ''" style="background: linear-gradient(135deg, #3b1d8a 0%, #2d1469 30%, #1a0d42 60%, #110830 100%); z-index: 6;">
            <!-- Ambient gradient blobs -->
            <div class="absolute inset-0 pointer-events-none z-0">
                <div class="absolute -top-24 -left-24 w-[500px] h-[500px] rounded-full bg-violet-500/30 blur-[120px]" />
                <div class="absolute -top-12 right-0 w-96 h-96 rounded-full bg-purple-600/20 blur-[100px]" />
                <div class="absolute bottom-0 left-1/3 w-[500px] h-40 bg-violet-700/25 blur-[80px]" />
                <div class="absolute top-1/2 right-1/4 w-64 h-64 rounded-full bg-indigo-500/15 blur-[80px]" />
            </div>

            <div class="relative mx-auto px-4 sm:px-6 pt-8 pb-10 sm:pt-10 sm:pb-14 text-white" :class="casting.is_for_performer && casting.performer ? 'max-w-3xl' : 'max-w-4xl'">

                <!-- Breadcrumb -->
                <nav class="flex items-center gap-1.5 text-xs text-white/40 mb-6 flex-wrap">
                    <Link
                        v-if="casting.user.slug"
                        :href="getMainAuditionsUrl(isUserLoggedIn, routePrefix)"
                        class="hover:text-white/70 transition-colors"
                    >Auditions</Link>
                    <span v-else class="text-white/40">{{ casting.user.username }}</span>
                    <span class="text-white/20">/</span>
                    <Link
                        v-if="casting.user.slug"
                        :href="getCastingsUrl(isUserLoggedIn, routePrefix)"
                        class="hover:text-white/70 transition-colors"
                    >Studio Pitches</Link>
                    <span v-else class="text-white/40">Studio Pitches</span>
                    <span class="text-white/20">/</span>
                    <span class="text-white/60 truncate max-w-[200px]">{{ casting.title }}</span>
                </nav>

                <!-- Performers -->
                <div v-if="casting.performers.length" class="flex items-center gap-3 mb-5">
                    <div v-if="!requiresAgeVerification" class="flex -space-x-2">
                        <div
                            v-for="performer in casting.performers.slice(0, 5)"
                            :key="performer.id"
                            class="w-11 h-11 rounded-full overflow-hidden ring-2 ring-violet-900/80 bg-zinc-800 shrink-0"
                        >
                            <img :src="performer.thumbnail_url" :alt="performer.name" class="w-full h-full object-cover object-top" />
                        </div>
                    </div>
                    <div class="min-w-0">
                        <p class="text-white/80 text-sm font-semibold leading-snug truncate">
                            {{ casting.performers.map(p => p.name).join(', ') }}
                        </p>
                        <p class="text-white/35 text-xs">{{ casting.performers.length }} performer{{ casting.performers.length !== 1 ? 's' : '' }}</p>
                    </div>
                </div>
                <div v-else-if="casting.performer_description" class="flex items-center gap-2 mb-5">
                    <span class="w-2 h-2 rounded-full bg-amber-400/60 shrink-0" />
                    <p class="text-white/50 text-sm italic line-clamp-1">{{ casting.performer_description }}</p>
                </div>

                <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-2">{{ casting.title }}</h1>
                <p class="text-white/40 text-sm mb-4 sm:mb-5">
                    Pitched by
                    <Link
                        v-if="casting.user.slug"
                        :href="route(routePrefix + '.profile.my', { slug: casting.user.slug })"
                        class="text-white/70 hover:text-white font-medium transition-colors"
                    >{{ casting.user.username }}</Link>
                    <span v-else class="text-white/70 font-medium">{{ casting.user.username }}</span>
                </p>

                <!-- Meta row -->
                <div class="flex items-start justify-between gap-3">
                    <div class="flex flex-wrap items-center gap-2.5">
                        <span v-if="casting.status === 8 && casting.auditions.length === 0" :class="['px-2.5 py-1 rounded-full border text-xs font-semibold', STATUS_MAP[4].class]">{{ STATUS_MAP[4].label }}</span>
                        <span v-else :class="['px-2.5 py-1 rounded-full border text-xs font-semibold', statusClass]">{{ statusLabel }}</span>
                        <span
                            v-if="casting.revised_by_admin"
                            class="px-2.5 py-1 rounded-full bg-emerald-600/90 border border-emerald-400/30 text-white text-xs font-bold"
                        >Revised by Staff</span>
                        <span v-if="casting.format" class="px-2.5 py-1 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-xs font-semibold">{{ casting.format }}</span>
                        <!-- supporters count — hidden, kept for reference (replaced by vote system) -->
                        <!--
                        <span class="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.08] text-white/50 text-xs font-medium">
                            <Users class="w-3 h-3" />
                            {{ formatNumber(casting.backers_count) }} supporter{{ casting.backers_count !== 1 ? 's' : '' }}
                        </span>
                        -->
                        <!-- stars received — hidden, kept for reference (replaced by vote system) -->
                        <!--
                        <span class="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/20 text-amber-300 text-xs font-semibold">
                            <img :src="STAR_ICON_URL" alt="" class="w-3 h-3" />
                            <template v-if="casting.star_goal > 0">{{ formatNumber(localStarsReceived) }} / {{ formatNumber(casting.star_goal) }} stars</template>
                            <template v-else>{{ formatNumber(localStarsReceived) }} stars</template>
                        </span>
                        -->
                        <span v-if="casting.auditions?.length" class="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-bold shadow shadow-emerald-500/20">
                            <span class="relative flex h-2 w-2">
                                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                            </span>
                            {{ casting.auditions.length }} audition{{ casting.auditions.length !== 1 ? 's' : '' }} in the race
                        </span>
                        <!--<span v-if="expiryLabel && casting.status === 1" class="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.06] text-white/35 text-xs font-medium">
                            <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path stroke-linecap="round" d="M12 6v6l4 2"/></svg>
                            {{ expiryLabel }}
                        </span>-->
                    </div>
                    <button
                        @click="openShare"
                        class="shrink-0 flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/25 hover:border-white/40 text-white text-sm font-semibold transition-all"
                    >
                        <Share2 class="w-4 h-4" />
                        Share
                    </button>
                </div>
            </div>
        </div>

        <div class="relative mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-10 text-white space-y-4 sm:space-y-5" :class="casting.is_for_performer && casting.performer ? 'max-w-3xl' : 'max-w-4xl'" style="z-index: 10;">

            <!-- ── Auditions Section ── -->
            <template v-if="casting.auditioning_performers?.length || casting.auditions?.length">
                <h2 class="flex items-center gap-2.5 text-sm font-bold text-white/70 uppercase tracking-widest pt-2">
                    <Clapperboard class="w-4 h-4 text-emerald-400" />
                    Auditions in the race
                </h2>

                <!-- Linked Auditions -->
                <div v-if="casting.auditions?.length" class="rounded-2xl page-card px-4 py-5 sm:px-6 sm:py-6">
                    <h2 class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-400/70 mb-5">
                        <PlayCircle class="w-4 h-4" />
                        Auditions
                        <span class="ml-auto text-white/20 font-normal normal-case tracking-normal text-[11px]">{{ casting.auditions.length }} audition{{ casting.auditions.length !== 1 ? 's' : '' }}</span>
                    </h2>

                    <div class="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 audition-carousel">
                        <Link
                            v-for="(audition, index) in sortedAuditions"
                            :key="audition.post_id"
                            :href="audition.slug ? route(routePrefix + '.reels.feed', { post: audition.slug }) : route(routePrefix + '.studio.casting.view', { slug: casting.slug })"
                            @click="audition.slug && storeFeedReferrer()"
                            :class="[
                                'group shrink-0 w-44 flex flex-col rounded-xl overflow-hidden transition-all',
                                audition.release_status === 3
                                    ? 'bg-zinc-900/40 border border-white/[0.04] opacity-50 grayscale-[60%]'
                                    : index === 0 && sortedAuditions.length > 1 && audition.likes > 0
                                        ? 'bg-zinc-900/80 border border-amber-400/40 hover:border-amber-400/70 shadow-lg shadow-amber-500/10'
                                        : 'bg-zinc-900/80 border border-white/[0.06] hover:border-emerald-500/30'
                            ]"
                        >
                            <div class="relative aspect-[3/4] bg-zinc-800 overflow-hidden">
                                <img
                                    v-if="audition.thumbnail_url"
                                    :src="requiresAgeVerification ? audition.locked_thumbnail_url : audition.thumbnail_url"
                                    :alt="audition.title"
                                    class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                                <div v-else class="w-full h-full flex items-center justify-center">
                                    <PlayCircle class="w-10 h-10 text-white/10 group-hover:text-white/20 transition-colors" />
                                </div>
                                <div class="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-transparent to-transparent" />
                                <!-- Leading / Winner crown badge -->
                                <div
                                    v-if="index === 0 && sortedAuditions.length > 0 && audition.likes > 0"
                                    class="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold shadow"
                                    :class="audition.star_goal_reached
                                        ? 'bg-yellow-300/95 text-zinc-900'
                                        : 'bg-amber-400/90 text-zinc-900'"
                                >
                                    {{ audition.star_goal_reached ? '🏆 Winner' : '👑 Leading' }}
                                </div>
                                <span
                                    class="absolute top-2 left-2 px-2 py-0.5 rounded-full border text-[10px] font-bold backdrop-blur-sm"
                                    :class="auditionStatusClass(audition)"
                                >
                                    {{ auditionStatusLabel(audition) }}
                                </span>
                            </div>

                            <div class="flex flex-col gap-2 px-3 py-3 flex-1">
                                <div class="min-w-0">
                                    <p class="text-white/85 text-xs font-semibold group-hover:text-white transition-colors line-clamp-2 leading-snug">
                                        {{ audition.title || 'Audition #' + audition.post_id }}
                                    </p>
                                    <p v-if="audition.creator_name" class="text-white/35 text-[11px] mt-0.5 truncate">{{ audition.creator_name }}</p>
                                </div>

                                <template v-if="audition.star_goal > 0 && !audition.release_status">
                                    <div class="mt-auto">
                                        <div class="flex items-center justify-between text-[10px] mb-1">
                                            <span class="text-white/30">{{ formatNumber(audition.likes) }} / {{ formatNumber(audition.star_goal) }}</span>
                                            <span class="text-white/20">{{ Math.min(100, Math.round(audition.likes / audition.star_goal * 100)) }}%</span>
                                        </div>
                                        <div class="h-1 bg-white/[0.08] rounded-full overflow-hidden">
                                            <div
                                                class="h-full rounded-full transition-all duration-500"
                                                :class="audition.star_goal_reached ? 'bg-gradient-to-r from-sky-500 to-sky-400' : 'bg-gradient-to-r from-blue-600 to-blue-500'"
                                                :style="{ width: Math.min(100, Math.round(audition.likes / audition.star_goal * 100)) + '%' }"
                                            />
                                        </div>
                                    </div>
                                </template>
                                <p v-else-if="audition.released_at" class="text-white/25 text-[10px] mt-auto">
                                    {{ new Date(audition.released_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }}
                                </p>
                            </div>
                        </Link>
                    </div>
                </div>

                <!-- Models Auditioning -->
                <div v-if="casting.auditioning_performers?.length" class="rounded-2xl page-card px-4 py-5 sm:px-6 sm:py-6">
                    <h2 class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-pink-400/70 mb-5">
                        <Users class="w-4 h-4" />
                        Models Auditioning
                        <span class="ml-auto text-white/20 font-normal normal-case tracking-normal text-[11px]">{{ casting.auditioning_performers.length }} model{{ casting.auditioning_performers.length !== 1 ? 's' : '' }}</span>
                    </h2>

                    <div class="flex flex-wrap gap-4">
                        <a
                            v-for="performer in casting.auditioning_performers"
                            :key="performer.id"
                            :href="route(routePrefix + '.performer.show', { slug: performer.slug })"
                            class="group flex flex-col items-center gap-2.5"
                        >
                            <div v-if="!requiresAgeVerification" class="w-28 rounded-xl overflow-hidden ring-1 ring-white/10 group-hover:ring-pink-500/40 bg-zinc-800 aspect-[2/3] transition-all">
                                <img
                                    :src="performer.thumbnail_url"
                                    :alt="performer.name"
                                    class="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                                />
                            </div>
                            <span class="text-white/70 group-hover:text-white text-sm font-semibold text-center leading-snug max-w-[7rem] transition-colors">{{ performer.name }}</span>
                        </a>
                    </div>
                </div>
            </template>

            <!-- ── Pitch Details Section ── -->
            <h2 class="flex items-center gap-2.5 text-sm font-bold text-white/70 uppercase tracking-widest pt-2">
                <Film class="w-4 h-4 text-violet-400" />
                Pitch Details
            </h2>

            <!-- Stars + progress — hidden, kept for reference (replaced by vote system) -->
            <!--
            <div class="rounded-2xl page-card px-4 py-4 sm:px-6 sm:py-5">
                <div class="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2">
                            <img :src="STAR_ICON_URL" alt="" class="w-5 h-5 shrink-0" />
                            <span class="text-white text-2xl font-bold tabular-nums">{{ formatNumber(localStarsReceived) }}</span>
                            <span v-if="casting.star_goal > 0" class="text-white/30 text-sm">/ {{ formatNumber(casting.star_goal) }} stars</span>
                            <span v-else class="text-white/30 text-sm">stars</span>
                        </div>
                        <div v-if="casting.star_goal > 0" class="h-2 mt-2 bg-white/[0.08] rounded-full overflow-hidden">
                            <div class="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full" :style="{ width: progressPct + '%' }" />
                        </div>
                    </div>
                    [support button — see below]
                </div>
            </div>

            [thumbs-up support button]
            <button @click="openSupport" ...>Show your support</button>
            -->

            <!-- ── Vote bar ── -->
            <div class="rounded-2xl page-card px-4 py-4 sm:px-6 sm:py-5">
                <div class="flex flex-col sm:flex-row sm:items-center gap-4">

                    <!-- Net score display -->
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2">
                            <img :src="STAR_ICON_URL" alt="" class="w-6 h-6 shrink-0" />
                            <span
                                class="text-4xl font-bold tabular-nums leading-none"
                                :class="{
                                    'text-blue-400':   localNetScore > 0,
                                    'text-orange-400': localNetScore < 0,
                                    'text-white/30':   localNetScore === 0,
                                }"
                            >{{ localNetScore }}</span>
                        </div>
                        <p v-if="!isOwner" class="text-white/30 text-xs mt-1.5">Use Stars to boost the pitches you want Naughty America to make next.</p>
                        <p v-if="voteError" class="text-red-400 text-xs mt-1.5">{{ voteError }}</p>
                    </div>

                    <!-- Boost / Not For Me buttons (non-owners only) -->
                    <div v-if="!isOwner" class="shrink-0 flex items-center gap-2">
                        <!-- Boost -->
                        <button
                            @click="submitVote(1)"
                            :disabled="voteLoading"
                            :class="[
                                'flex items-center gap-2 px-5 py-3 rounded-xl border transition-all active:scale-95 disabled:opacity-50 font-semibold',
                                localVote === 1
                                    ? 'bg-blue-500/20 border-blue-500/40 text-blue-300'
                                    : 'bg-transparent border-white/15 text-white/50 hover:border-blue-500/40 hover:text-blue-300',
                            ]"
                            title="Boost this pitch with Stars"
                        >
                            <img :src="STAR_ICON_URL" alt="" class="w-4 h-4 shrink-0" />
                            {{ localVote === 1 ? 'Boosted' : 'Boost' }}<template v-if="localVote !== 1"><span class="opacity-50 ml-1">· {{ supportCost === -1 ? '?' : supportCost }}</span></template>
                        </button>

                        <!-- Not For Me -->
                        <button
                            @click="submitVote(-1)"
                            :disabled="voteLoading"
                            :class="[
                                'flex items-center gap-2 px-5 py-3 rounded-xl border transition-all active:scale-95 disabled:opacity-50 font-semibold',
                                localVote === -1
                                    ? 'bg-orange-500/20 border-orange-500/40 text-orange-300'
                                    : 'bg-transparent border-white/15 text-white/50 hover:border-orange-500/40 hover:text-orange-300',
                            ]"
                            title="Mark this pitch as not for me"
                        >
                            <img :src="STAR_ICON_URL" alt="" class="w-4 h-4 shrink-0 opacity-50" />
                            Not for me<template v-if="localVote !== -1"><span class="opacity-50 ml-1">· {{ supportCost === -1 ? '?' : supportCost }}</span></template>
                        </button>
                    </div>

                </div>
            </div>

            <!-- Cast -->
            <div class="rounded-2xl page-card px-4 py-5 sm:px-6 sm:py-6">
                <h2 class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-violet-400/70 mb-5">
                    <Users class="w-4 h-4" />
                    Inspiration Cast
                </h2>

                <template v-if="casting.performers.length">
                    <div class="flex flex-wrap gap-4">
                        <a
                            v-for="performer in casting.performers"
                            :key="performer.id"
                            :href="route(routePrefix + '.performer.show', { slug: performer.slug })"
                            class="group flex flex-col items-center gap-2.5"
                        >
                            <div v-if="!requiresAgeVerification" class="w-28 rounded-xl overflow-hidden ring-1 ring-white/10 group-hover:ring-violet-500/40 bg-zinc-800 aspect-[2/3] transition-all">
                                <img :src="performer.thumbnail_url" :alt="performer.name" class="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                            </div>
                            <span class="text-white/80 group-hover:text-white text-sm font-semibold text-center leading-snug max-w-[7rem] transition-colors">{{ performer.name }}</span>
                        </a>
                    </div>
                </template>
                <template v-else-if="casting.performer_description">
                    <div class="flex items-start gap-3 bg-amber-500/5 border border-amber-500/15 rounded-xl px-5 py-4">
                        <Pencil class="w-4 h-4 text-amber-400/60 shrink-0 mt-0.5" />
                        <p class="text-white/60 text-base leading-relaxed">{{ casting.performer_description }}</p>
                    </div>
                </template>
                <p v-else class="text-white/20 text-sm italic">No cast specified</p>
            </div>

            <!-- Pitch + Scene Details: 2-col on desktop -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">

                <!-- The Pitch -->
                <div class="rounded-2xl page-card px-4 py-5 sm:px-6 sm:py-6">
                    <h2 class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-400/70 mb-5">
                        <Lightbulb class="w-4 h-4" />
                        The Pitch
                    </h2>
                    <p v-if="casting.description" class="text-white/70 text-base leading-relaxed">{{ casting.description }}</p>
                    <p v-else class="text-white/20 text-sm italic">No description provided</p>

                    <div v-if="casting.director_notes" class="mt-5">
                        <p class="text-white/30 text-xs font-semibold uppercase tracking-wider mb-2.5">Director's Notes</p>
                        <div class="bg-white/[0.03] border border-white/[0.06] rounded-xl px-5 py-4">
                            <p class="text-white/60 text-base leading-relaxed">{{ casting.director_notes }}</p>
                        </div>
                    </div>
                </div>

                <!-- Scene Details -->
                <div class="rounded-2xl page-card px-4 py-5 sm:px-6 sm:py-6">
                    <h2 class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-teal-400/70 mb-5">
                        <Film class="w-4 h-4" />
                        Scene Details
                    </h2>

                    <div class="space-y-5">
                        <div v-if="casting.setting?.length">
                            <p class="text-white/30 text-xs font-semibold uppercase tracking-wider mb-2.5">Setting</p>
                            <div class="flex flex-wrap gap-2">
                                <span
                                    v-for="s in casting.setting"
                                    :key="s"
                                    class="px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm font-medium"
                                >{{ s }}</span>
                            </div>
                        </div>

                        <div v-if="casting.tags?.length">
                            <p class="text-white/30 text-xs font-semibold uppercase tracking-wider mb-2.5">Tags</p>
                            <div class="flex flex-wrap gap-2">
                                <span
                                    v-for="tag in casting.tags"
                                    :key="tag.id ?? tag"
                                    class="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/50 text-sm font-medium"
                                >#{{ tag.name ?? tag }}</span>
                            </div>
                        </div>

                        <p v-if="!casting.setting?.length && !casting.tags?.length" class="text-white/20 text-sm italic">No scene details provided</p>
                    </div>
                </div>

            </div>

            <!-- Footer row: info -->
            <div class="rounded-2xl page-card px-4 py-5 sm:px-6 sm:py-5 space-y-3 text-sm">
                <div class="flex items-center justify-between">
                    <span class="text-white/30">Submitted by</span>
                    <Link
                        v-if="casting.user.slug"
                        :href="route(routePrefix + '.profile.my', { slug: casting.user.slug })"
                        class="text-violet-400 font-medium hover:text-violet-300 underline underline-offset-2 decoration-violet-400/40 hover:decoration-violet-300/60 transition-colors"
                    >{{ casting.user.username }}</Link>
                    <span v-else class="text-white/60 font-medium">{{ casting.user.username }}</span>
                </div>
                <div class="flex items-center justify-between">
                    <span class="text-white/30">Submitted</span>
                    <span class="text-white/60">{{ submittedDate }}</span>
                </div>
                <div v-if="casting.format" class="flex items-center justify-between">
                    <span class="text-white/30">Format</span>
                    <span class="text-teal-300 font-semibold">{{ casting.format }}</span>
                </div>
                <div class="flex items-center justify-between">
                    <span class="text-white/30">Status</span>
                    <span v-if="casting.status === 8 && casting.auditions.length === 0" :class="['font-semibold', STATUS_MAP[4].color]">{{ STATUS_MAP[4].label }}</span>
                    <span v-else :class="['font-semibold', statusTextColor]">{{ statusLabel }}</span>
                </div>
            </div>

            <!-- Footer -->
            <hr class="border border-white/5 mt-16">
            <footer class="mt-8 px-4 sm:px-6 max-w-4xl mx-auto pb-12">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-8 pb-8">
                    <div>
                        <h3 class="text-white/40 text-xs font-semibold mb-4 uppercase tracking-wider">About</h3>
                        <p class="text-white/25 text-xs leading-relaxed">
                            With a history of over 25 years, Naughty America is recognized as one of the most
                            award-winning adult entertainment platforms online.
                        </p>
                    </div>
                    <div>
                        <h3 class="text-white/40 text-xs font-semibold mb-4 uppercase tracking-wider">Explore</h3>
                        <div class="grid grid-cols-2 gap-y-2 text-xs">
                            <a :href="route('www.main.home')" class="text-white/25 hover:text-white/60 transition-colors">Home</a>
                            <a :href="route('www.scene.index')" class="text-white/25 hover:text-white/60 transition-colors">Videos</a>
                            <a :href="route('www.join.signUp')" class="text-white/25 hover:text-white/60 transition-colors">Join</a>
                            <a :href="route('www.fantasy.index')" class="text-white/25 hover:text-white/60 transition-colors">Categories</a>
                            <a :href="route('www.performer.index', 'top-rated')" class="text-white/25 hover:text-white/60 transition-colors">Pornstars</a>
                            <a :href="route('www.site.index')" class="text-white/25 hover:text-white/60 transition-colors">Sites</a>
                        </div>
                    </div>
                    <div>
                        <h3 class="text-white/40 text-xs font-semibold mb-4 uppercase tracking-wider">Legal</h3>
                        <div class="space-y-2 text-xs">
                            <a :href="route('www.main.privacyPolicy')" class="block text-white/25 hover:text-white/60 transition-colors">Privacy Policy</a>
                            <a :href="route('www.main.termsOfService')" class="block text-white/25 hover:text-white/60 transition-colors">Terms of Service</a>
                            <a :href="route('www.main.usc2257')" class="block text-white/25 hover:text-white/60 transition-colors">18 USC 2257</a>
                        </div>
                    </div>
                </div>
                <div class="border-t border-white/5 py-6 flex items-center justify-between text-[11px] text-white/15">
                    <span>&copy; {{ new Date().getFullYear() }} La Touraine, Inc.</span>
                    <a class="rta-label" href="http://www.rtalabel.org/" target="_blank" rel="noopener">
                        <img width="60" height="28" src="https://sm.naughtycdn.com/images/members2/rta-light.png" alt="RTA" />
                    </a>
                </div>
            </footer>

        </div>
    </div>

    <SharePopup />

    <!-- Create pitch floater -->
    <Teleport to="body">
        <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="translate-y-full opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-full opacity-0"
        >
            <div
                v-if="scrolled && !isOwner"
                class="fixed bottom-0 left-0 right-0 z-40 flex justify-center px-4 pb-4 pointer-events-none"
            >
                <div class="flex items-center gap-3 sm:gap-4 px-4 py-3 rounded-2xl bg-zinc-900/95 backdrop-blur-md border border-violet-500/20 shadow-2xl shadow-black/60 pointer-events-auto">
                    <div class="w-8 h-8 rounded-lg bg-violet-600/20 border border-violet-500/25 flex items-center justify-center shrink-0">
                        <Clapperboard class="w-4 h-4 text-violet-400" />
                    </div>
                    <div class="min-w-0 hidden sm:block">
                        <p class="text-white text-sm font-semibold leading-none mb-0.5">Have your own idea?</p>
                        <p class="text-white/35 text-xs">Pitch your scene and get community support.</p>
                    </div>
                    <p class="text-white text-sm font-semibold sm:hidden">Have your own idea?</p>
                    <Link
                        :href="route(routePrefix + '.studio.create-scene')"
                        class="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-bold transition-colors whitespace-nowrap"
                    >
                        <Clapperboard class="w-4 h-4" />
                        Create Pitch
                    </Link>
                </div>
            </div>
        </Transition>
    </Teleport>

    <CastingSignupModal
        :isOpen="isSignupModalOpen"
        :source="sfwJoinSource || voteJoinSource || defaultJoinSource"
        :sfwTrigger="requiresAgeVerification"
        @close="handleSignupModalClose"
    />
    <CastingSupportModal
        :isOpen="isSupportModalOpen"
        :casting="casting"
        :availableStars="userBalance"
        :loading="supportModalLoading"
        :error="supportModalError"
        :success="supportModalSuccess"
        @close="handleSupportModalClose"
        @submit="handleSupportModalSubmit"
        @resetSuccess="handleSupportModalResetSuccess"
    />
    <BuyStarsModal
        :isOpen="isBuyStarsModalOpen"
        :userBalance="userBalance"
        :starPacks="props.starPacks"
        @close="isBuyStarsModalOpen = false"
        @buy="handleBuyStarPack"
    />
</template>


<script setup>
import { ref, computed, defineAsyncComponent, onMounted, onUnmounted } from 'vue'
import { usePage, Link } from '@inertiajs/vue3'
import axios from 'axios'
import { Users, Lightbulb, Film, Pencil, Clapperboard, PlayCircle, Share2 } from 'lucide-vue-next'
import HeaderV2 from '@/Components/Reels/HeaderV2.vue'
import CastingSupportModal from '@/Components/Casting/CastingSupportModal.vue'
import CastingSignupModal from '@/Components/Casting/CastingSignupModal.vue'
import BuyStarsModal from '@/Components/Reels/BuyStarsModal.vue'
import {getCastingsUrl, getMainAuditionsUrl} from "@/Helpers/auditions";
import { openStarPackPurchaseUrl } from '@/Helpers/starPackPurchase'
import {getFathomPagePrefix, trackFathomEvent} from "@/Helpers/analytics"
import { useRecordSgaGoal } from '@/composables/useRecordSgaGoal'

const SharePopup = defineAsyncComponent(() => import('@/Components/Common/SharePopup.vue'))

const STAR_ICON_URL = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const scrolled = ref(false)
const onScroll = () => { scrolled.value = window.scrollY > 50 }

const onSharePopupUsed = () => {
    if (!isUserLoggedIn.value) return
    recordSgaGoal('share_pitch', { casting_id: props.casting.id })
}

onMounted(() => {
    if (requiresAgeVerification.value) {
        sfwJoinSource.value = 'view_casting_sfw_join_modal'
        isSignupModalOpen.value = true;
    }
    document.body.classList.add('view-casting-page')
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('share-popup:used', onSharePopupUsed)
})
onUnmounted(() => {
    document.body.classList.remove('view-casting-page')
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('share-popup:used', onSharePopupUsed)
})

const sharedProps = usePage().props

const isUserLoggedIn          = computed(() => !!(sharedProps.auth?.user?.id))
const isOwner                 = computed(() => isUserLoggedIn.value && Number(sharedProps.auth?.user?.id) === Number(props.casting.user_id))
const isGoldUser              = computed(() => !!(sharedProps.auth?.is_gold))
const routePrefix             = computed(() => sharedProps.routePrefix || 'www')
const requiresAgeVerification = computed(() => !!(sharedProps.requiresAgeVerification))

const props = defineProps({
    casting:          { type: Object, required: true },
    castingsSettings: { type: Object, required: true },
    starPacks:        { type: Array,  default: () => [] },
})

const defaultJoinSource = ref('view_casting_support')
const sfwJoinSource = ref('')
const voteJoinSource = ref('')

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatNumber = (n) => n ? Number(n).toLocaleString('en-US') : '0'

const auditionStatusLabel = (audition) => {
    const map = { 1: 'In Production', 2: 'Released', 3: 'Race Lost' }
    if (map[audition.release_status]) return map[audition.release_status]
    return audition.star_goal_reached ? 'Sponsored' : 'Open'
}

const auditionStatusClass = (audition) => {
    if (audition.release_status === 2) return 'bg-blue-500/30 border-blue-500/40 text-blue-200'
    if (audition.release_status === 1) return 'bg-amber-500/30 border-amber-500/40 text-amber-200'
    if (audition.release_status === 3) return 'bg-red-500/30 border-red-500/40 text-red-200'
    if (audition.star_goal_reached)    return 'bg-sky-500/30 border-sky-500/40 text-sky-200'
    return 'bg-emerald-500/30 border-emerald-500/40 text-emerald-200'
}

const timeAgo = (dateStr) => {
    if (!dateStr) return ''
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
    if (diff < 60)      return 'just now'
    if (diff < 3600)    return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400)   return `${Math.floor(diff / 3600)}h ago`
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const submittedDate = computed(() => {
    if (!props.casting.created_at) return '—'
    return new Date(props.casting.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
})

// ── Status ────────────────────────────────────────────────────────────────────

const STATUS_MAP = {
    0: { label: 'Draft',         class: 'text-white/50 bg-white/5 border-white/10',                   color: 'text-white/50' },
    1: { label: 'Open',          class: 'text-amber-400 bg-amber-500/10 border-amber-500/20',         color: 'text-amber-400' },
    2: { label: 'Talent Tagged', class: 'text-purple-400 bg-purple-500/10 border-purple-500/20',      color: 'text-purple-400' },
    3: { label: 'Expired',       class: 'text-white/30 bg-white/5 border-white/10',                   color: 'text-white/30' },
    4: { label: 'In Production', class: 'text-orange-300 bg-orange-500/20 border-orange-400/40',      color: 'text-orange-400' },
    5: { label: 'Released',      class: 'text-blue-400 bg-blue-500/10 border-blue-500/20',            color: 'text-blue-400' },
    6: { label: 'Now Casting',   class: 'text-amber-400 bg-amber-500/10 border-amber-500/20',         color: 'text-amber-400' },
    7: { label: 'In The Race',   class: 'text-orange-300 bg-orange-500/20 border-orange-400/40',      color: 'text-orange-400' },
    8: { label: 'Race Over',     class: 'text-green-300 bg-green-500/20 border-green-400/40',         color: 'text-green-400' },
}

const statusLabel     = computed(() => STATUS_MAP[props.casting.status]?.label ?? 'Unknown')
const statusClass     = computed(() => STATUS_MAP[props.casting.status]?.class ?? 'text-white/50 bg-white/5 border-white/10')
const statusTextColor = computed(() => STATUS_MAP[props.casting.status]?.color ?? 'text-white/50')

// ── Expiry ────────────────────────────────────────────────────────────────────

const expiryLabel = computed(() => {
    if (!props.casting.expires_at) return null
    const diff = new Date(props.casting.expires_at) - Date.now()
    if (diff <= 0) return 'Expired'
    const days = Math.floor(diff / 86400000)
    if (days > 0) return `${days}d left`
    return `${Math.floor(diff / 3600000)}h left`
})

// ── Progress (local reactive copy so it updates after support) ─────────────────

const localStarsReceived = ref(props.casting.stars_received)
const localBackersCount  = ref(props.casting.backers_count ?? 0)

const progressPct = computed(() => {
    if (!props.casting.star_goal) return 0
    return Math.min(100, Math.round(localStarsReceived.value / props.casting.star_goal * 100))
})

// ── Voting ────────────────────────────────────────────────────────────────────
const { recordSgaGoal } = useRecordSgaGoal()

const localVote      = ref(props.casting.user_vote     ?? 0)
const localNetScore  = ref(props.casting.net_score     ?? 0)
const localUpvotes   = ref(props.casting.upvotes_count ?? 0)
const localDownvotes = ref(props.casting.downvotes_count ?? 0)
const voteLoading    = ref(false)
const voteError      = ref('')

const submitVote = async (voteValue) => {
    const event = voteValue === 1 ? 'Boost' : 'Not_For_Me'
    trackFathomEvent(getFathomPagePrefix(routePrefix.value) + '_Casting_Page_' + event + '_Button_Clicked')

    if (!isUserLoggedIn.value) {
        voteJoinSource.value = 'Casting_Page_' + event
        isSignupModalOpen.value = true
        return
    }
    if (voteLoading.value) return

    voteError.value = ''

    const prevVote      = localVote.value
    const prevNetScore  = localNetScore.value
    const prevUpvotes   = localUpvotes.value
    const prevDownvotes = localDownvotes.value
    const newVote       = prevVote === voteValue ? 0 : voteValue
    // Optimistic update
    localVote.value      = newVote
    localNetScore.value  = prevNetScore + (newVote - prevVote)
    localUpvotes.value   = prevUpvotes   + (newVote === 1 ? 1 : prevVote === 1 ? -1 : 0)
    localDownvotes.value = prevDownvotes + (newVote === -1 ? 1 : prevVote === -1 ? -1 : 0)
    voteLoading.value    = true

    try {
        const response = await axios.post(route('members2.reels.casting.vote', { id: props.casting.id }), { vote: voteValue })
        localVote.value      = response.data.user_vote
        localNetScore.value  = response.data.net_score
        localUpvotes.value   = response.data.upvotes_count
        localDownvotes.value = response.data.downvotes_count

        if (response.data.remaining_balance !== undefined) {
            syncBalance(response.data.remaining_balance)
        }

        if (response.data.user_vote !== 0) {
            recordSgaGoal('vote_pitch', { casting_id: props.casting.id })
        }
    } catch (e) {
        localVote.value      = prevVote
        localNetScore.value  = prevNetScore
        localUpvotes.value   = prevUpvotes
        localDownvotes.value = prevDownvotes
        voteError.value = e.response?.data?.message || 'Something went wrong.'
        if (e.response?.data?.message?.includes('enough Stars')) {
            isBuyStarsModalOpen.value = true
        }
    } finally {
        voteLoading.value = false
    }
}

const sortedAuditions = computed(() =>
    [...(props.casting.auditions ?? [])].sort((a, b) => (b.likes ?? 0) - (a.likes ?? 0))
)

// ── Support ───────────────────────────────────────────────────────────────────

const supportCost         = computed(() => Number(props.castingsSettings?.support_cost ?? 1))
const userBalance         = ref(Number(sharedProps.auth?.user?.wallet_balance) || 0)

const syncBalance = (newBalance) => {
    userBalance.value = newBalance
    if (sharedProps.auth?.user) {
        sharedProps.auth.user.wallet_balance = newBalance
    }
}

const isSignupModalOpen   = ref(false)
const isSupportModalOpen  = ref(false)
const isBuyStarsModalOpen = ref(false)
const supportModalLoading = ref(false)
const supportModalError   = ref('')
const supportModalSuccess = ref(false)
const supported           = ref(false)
const showSupportedMessage = ref(false)
let supportedMessageTimer = null
const showSupportedThanks = () => {
    showSupportedMessage.value = true
    clearTimeout(supportedMessageTimer)
    supportedMessageTimer = setTimeout(() => { showSupportedMessage.value = false }, 4000)
}
const supportError        = ref('')
const supportLoading      = ref(false)
const confirming          = ref(false)

const storeFeedReferrer = () => {
    sessionStorage.setItem('feedReferrer', window.location.href)
}

const openShare = () => {
    trackFathomEvent(getFathomPagePrefix(routePrefix.value) + '_Casting_Page_Share_Button_Clicked')
    document.getElementById('ShareLinkModal')?.classList.remove('hidden')

}

const openSupport = () => {
    if (!isUserLoggedIn.value) {
        isSignupModalOpen.value = true
        return
    }
    if (supportCost.value === -1) {
        supportModalError.value = ''
        supportModalSuccess.value = false
        isSupportModalOpen.value = true
    } else {
        confirming.value = true
    }
}

const submitDirectSupport = async () => {
    supportLoading.value = true
    supportError.value = ''
    try {
        const response = await axios.post(`/feed/api/castings/${props.casting.id}/support`)
        localStarsReceived.value = response.data.stars_received ?? (localStarsReceived.value + 1)
        localBackersCount.value  = localBackersCount.value + 1
        if (response.data.remaining_balance !== undefined) {
            userBalance.value = response.data.remaining_balance
        }
        supported.value = true
        confirming.value = false
        showSupportedThanks()
    } catch (e) {
        supportError.value = e.response?.data?.message || 'Something went wrong.'
    } finally {
        supportLoading.value = false
    }
}

const handleSupportModalSubmit = async (amount) => {
    supportModalLoading.value = true
    supportModalError.value = ''
    try {
        const response = await axios.post(`/feed/api/castings/${props.casting.id}/support`, { stars: amount })
        supportModalSuccess.value = true
        localStarsReceived.value = response.data.stars_received ?? (localStarsReceived.value + amount)
        localBackersCount.value  = localBackersCount.value + 1
        if (response.data.remaining_balance !== undefined) {
            userBalance.value = response.data.remaining_balance
        }
        supported.value = true
        showSupportedThanks()
    } catch (e) {
        supportModalError.value = e.response?.data?.message || 'Something went wrong. Please try again.'
    } finally {
        supportModalLoading.value = false
    }
}

const handleSupportModalClose = () => {
    isSupportModalOpen.value = false
    supportModalError.value = ''
    supportModalSuccess.value = false
}

const handleSupportModalResetSuccess = () => {
    supportModalSuccess.value = false
    supportModalError.value = ''
}

const handleBuyStarPack = (pack) => {
    openStarPackPurchaseUrl(pack, 'pitch')
}

const handleSignupModalClose = () => {
    isSignupModalOpen.value = false
    sfwJoinSource.value = ''
    voteJoinSource.value = ''
}
</script>

<style>
.view-casting-page #masterlayout { padding-bottom: 0 !important; }
</style>

<style scoped>
.page-bg {
    background: linear-gradient(160deg, #16101f 0%, #111827 100%);
}

.page-card {
    background: rgba(255, 255, 255, 0.04);
}

.audition-carousel {
    scrollbar-width: none;
}
.audition-carousel::-webkit-scrollbar {
    display: none;
}
</style>
